/**
 * SEO + JSON-LD `Product` pour la fiche produit dédiée
 * `/boutique/[slug]/[productSlug]`.
 *
 * Calqué À L'IDENTIQUE sur `useShopCheckoutSeo` (/boutique) et
 * `useEventCheckoutSeo` (/b) : title enrichi asso, canonical prod en dur,
 * og:image auto-référente (`useShareBaseUrl`), noindex sur les statuts non
 * achetables — plus, ici, le balisage `Product` + `Offer` attendu par les
 * Rich Results (name, image, description, price, priceCurrency, availability).
 *
 * ⚠️ MONTANTS EN EUROS.
 */
import type { Shop, ShopProduct, ShopStatus } from "~/types/shop";
import { shopProductMinPrice, shopProductSoldOut } from "~/utils/shopPricing";
import { shopProductSlug } from "~/utils/shopProductUrl";

export function useShopProductSeo(
  shop: Ref<Shop | null>,
  product: Ref<ShopProduct | null>,
  slug: Ref<string>,
) {
  const { t } = useI18n();
  const config = useRuntimeConfig();
  // Origine du site COURANT pour les og:image auto-référentes (staging/preview
  // doivent pointer vers eux-mêmes) — cf. useShareBaseUrl. Canonical = prod.
  const shareBase = useShareBaseUrl();

  const assetsBaseUrl = computed(
    () => (config.public.apiUrl as string) || "https://api.donoor.org",
  );

  const shopUrl = computed(() => `https://donoor.org/boutique/${slug.value}`);
  const canonicalUrl = computed(() =>
    product.value
      ? `${shopUrl.value}/${shopProductSlug(product.value)}`
      : shopUrl.value,
  );

  const plainDescription = computed(() => {
    const raw = product.value?.description?.replace(/<[^>]*>/g, "").trim() || "";
    if (raw) return raw.substring(0, 160);
    const org = shop.value?.organization_name || t("boutique.commun.associationGenerique");
    const name = product.value?.name || t("boutique.productSeo.ceProduit");
    return t("boutique.productSeo.descriptionParDefaut", { nom: name, association: org }).substring(
      0,
      160,
    );
  });

  /** Première photo produit en 1200×630 — sinon l'OG de la boutique. */
  const ogImageUrl = computed(() => {
    const imageId = product.value?.images?.[0];
    if (imageId) {
      return `${assetsBaseUrl.value}/api/assets/${imageId}?width=1200&height=630&fit=cover&format=jpg&quality=80`;
    }
    if (slug.value) return `${shareBase}/api/og/shop?slug=${slug.value}`;
    return `${shareBase}/og-image.png`;
  });

  /** Toutes les photos produit (haute résolution) pour `Product.image`. */
  const productImages = computed(() =>
    (product.value?.images || []).map(
      (id) => `${assetsBaseUrl.value}/api/assets/${id}?width=1200&height=1200&fit=cover`,
    ),
  );

  // Title : « {Produit} — {Boutique} — {Asso} » tronqué à 60 (parité /b).
  const seoTitle = computed(() => {
    if (!product.value) return t("boutique.productSeo.titreDefaut");
    const parts: string[] = [product.value.name];
    const org = shop.value?.organization_name?.trim();
    if (org && !product.value.name.toLowerCase().includes(org.toLowerCase())) {
      parts.push(org);
    } else if (shop.value?.title) {
      parts.push(shop.value.title);
    }
    return parts.join(" — ").substring(0, 60);
  });

  // Noindex si la boutique n'est pas publiquement achetable, ou si le produit
  // n'existe pas / est désactivé (on ne ranke pas une page morte).
  const shouldNoindex = computed(() => {
    if (!shop.value || !product.value) return true;
    if (!product.value.is_active) return true;
    // Boutique PRIVÉE (migration 285) : accessible par son lien, jamais
    // indexée — c'est sa raison d'être.
    if (shop.value.is_public === false) return true;
    const blockedStatuses: ShopStatus[] = ["draft", "closed", "archived"];
    return blockedStatuses.includes(shop.value.status);
  });

  const robotsContent = useRobotsContent(shouldNoindex);

  useHead({
    title: seoTitle,
    bodyAttrs: {
      style: computed(() => (shop.value ? "background-color: #f7f9fd" : "")),
    },
    link: computed(() => [
      { rel: "canonical", key: "canonical", href: canonicalUrl.value },
    ]),
    meta: computed(() => [{ name: "robots", content: robotsContent.value }]),
    script: computed(() => {
      if (!shop.value || !product.value || shouldNoindex.value) return [];

      const orgName = shop.value.organization_name || "Donoor";
      const orgUrl = shop.value.organization_slug
        ? `https://donoor.org/associations/${shop.value.organization_slug}`
        : "https://donoor.org";

      // BreadcrumbList : Accueil → Asso → Boutique → Produit
      const breadcrumbLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: t("boutique.commun.accueil"), item: "https://donoor.org" },
          { "@type": "ListItem", position: 2, name: orgName, item: orgUrl },
          { "@type": "ListItem", position: 3, name: shop.value.title, item: shopUrl.value },
          { "@type": "ListItem", position: 4, name: product.value.name, item: canonicalUrl.value },
        ],
      };

      // Product + Offer — la donnée structurée attendue par les Rich Results.
      // `availability` reflète le stock RÉEL (toutes variantes épuisées →
      // OutOfStock), jamais un « InStock » de façade.
      const soldOut = shopProductSoldOut(product.value);
      const productLd: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.value.name,
        description: plainDescription.value,
        url: canonicalUrl.value,
        ...(productImages.value.length > 0 ? { image: productImages.value } : {}),
        ...(product.value.category ? { category: product.value.category } : {}),
        brand: { "@type": "Organization", name: orgName },
        offers: {
          "@type": "Offer",
          url: canonicalUrl.value,
          price: shopProductMinPrice(product.value).toFixed(2),
          priceCurrency: "EUR",
          availability: soldOut
            ? "https://schema.org/OutOfStock"
            : "https://schema.org/InStock",
          seller: { "@type": "Organization", name: orgName, url: orgUrl },
        },
      };

      return [
        { type: "application/ld+json", innerHTML: JSON.stringify(breadcrumbLd) },
        { type: "application/ld+json", innerHTML: JSON.stringify(productLd) },
      ];
    }),
  });

  useSeoMeta({
    title: () => seoTitle.value,
    description: () => plainDescription.value,
    ogType: "website",
    ogTitle: () => product.value?.name || t("boutique.productSeo.titreDefaut"),
    ogDescription: () => plainDescription.value,
    ogImage: () => ogImageUrl.value,
    ogImageWidth: 1200,
    ogImageHeight: 630,
    ogUrl: () => canonicalUrl.value,
    ogLocale: "fr_FR",
    twitterCard: "summary_large_image",
    twitterTitle: () => product.value?.name || t("boutique.productSeo.titreDefaut"),
    twitterDescription: () => plainDescription.value,
    twitterImage: () => ogImageUrl.value,
    robots: () => robotsContent.value,
  });

  return { canonicalUrl, ogImageUrl, plainDescription };
}
